/**
 * Loads a promoted prototype bundle from S3 using the fetch + blob URL + dynamic import pattern.
 *
 * This mirrors the rapid hub's PrototypeLoader approach:
 * 1. Fetch the ES module source as text
 * 2. Create a blob URL so the browser can import it as a module
 * 3. Dynamic import the blob URL
 * 4. Always revoke the blob URL to prevent memory leaks
 * 5. Validate the module has a default export function
 */

/**
 * A loaded prototype module.
 *
 * The default export can be either:
 * - A mount function with the 3-arg signature (promoted prototypes)
 * - A map of section name → createSection function (preview bundles)
 */
export interface PrototypeModule {
  default:
    | ((container: HTMLElement, shadowRoot: ShadowRoot, context: unknown) => void)
    | Record<string, (...args: unknown[]) => void>
}

/** Dynamic import function type, injectable for testing */
export type DynamicImportFn = (url: string) => Promise<Record<string, unknown>>

/**
 * Default dynamic import implementation using the native import() expression.
 * Extracted to allow test injection since vitest/jsdom cannot import blob URLs.
 */
export const defaultDynamicImport: DynamicImportFn = (url: string) => import(/* @vite-ignore */ url)

/**
 * Fetches a prototype bundle from the given URL, imports it as an ES module via blob URL,
 * and returns the module if it has a valid default export function.
 *
 * @param bundleUrl - The URL to fetch the prototype bundle from
 * @param dynamicImport - Optional import function override (for testing)
 * @returns The loaded prototype module with a default mount function
 *
 * @throws Error if the HTTP response is not ok (includes status code)
 * @throws Error if the module does not export a default function
 */
export async function loadPrototypeBundle(
  bundleUrl: string,
  dynamicImport: DynamicImportFn = defaultDynamicImport,
): Promise<PrototypeModule> {
  const response = await fetch(bundleUrl)
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} fetching promoted prototype bundle`)
  }

  const code = await response.text()
  const blob = new Blob([code], { type: "application/javascript" })
  const blobUrl = URL.createObjectURL(blob)

  let module: Record<string, unknown>
  try {
    module = await dynamicImport(blobUrl)
  } finally {
    URL.revokeObjectURL(blobUrl)
  }

  if (typeof module.default !== "function" && (typeof module.default !== "object" || module.default === null)) {
    throw new Error("Promoted prototype bundle missing default export (expected function or object)")
  }

  return module as unknown as PrototypeModule
}
